import type {
    Space,
    SpaceNode,
    SpaceNodePayload,
    SpacePayload,
} from '@/spaces/types/types';
import { useSpacesActivityLog } from '@/spaces/use/use-spaces-activity-log';
import { useList } from '@/voc/use/use-list.js';
import { useVueFlow } from '@vue-flow/core';
import { computed, ref, type ComputedRef, type Ref } from 'vue';
import {
    addNodeToSpace as addNodeToSpaceApi,
    clearAllSpaces as clearAllSpacesApi,
    createSpace as createSpaceApi,
    deleteSpace as deleteSpaceApi,
    deleteSpaceItem as deleteSpaceItemApi,
    fetchSpaceById as fetchSpaceByIdApi,
    fetchSpaces as fetchSpacesApi,
    setSpaceStarred as setSpaceStarredApi,
    updateSpace as updateSpaceApi,
    updateSpaceItem as updateSpaceItemApi,
    summarizeSpace as summarizeSpaceApi,
    assessSpace as assessSpaceApi,
    fetchSpaceItem as fetchSpaceItemApi,
} from '../api/spaces.api';

const fetchingSpaces = ref(false);

/** Monotonic id for `setSpaceStarred` so out-of-order responses cannot overwrite newer UI state. */
let nextStarToggleMutationId = 0;
const pendingStarToggleMutationIdBySpace = new Map<string, number>();

/** Monotonic id for `setSpaceVisibility` (same race protection as starred toggles). */
let nextVisibilityMutationId = 0;
const pendingVisibilityMutationIdBySpace = new Map<string, number>();

/**
 * Last server-confirmed starred / visibility (not optimistic). Rollbacks use these so a failed
 * toggle after rapid clicks does not restore another toggle's optimistic value.
 */
const serverConfirmedStarredBySpace = new Map<string, boolean>();
const serverConfirmedVisibilityBySpace = new Map<string, 'public' | 'private'>();

function recordServerStarVisibilityBaselines (space: Space): void {
    serverConfirmedStarredBySpace.set(space.id, space.isStarred === true);
    serverConfirmedVisibilityBySpace.set(space.id, space.visibility ?? 'private');
}

/** Clears in-flight toggle + server-baseline maps; for Jest isolation across examples. */
export function resetSpacesToggleStateForTests (): void {
    pendingStarToggleMutationIdBySpace.clear();
    pendingVisibilityMutationIdBySpace.clear();
    serverConfirmedStarredBySpace.clear();
    serverConfirmedVisibilityBySpace.clear();
    nextStarToggleMutationId = 0;
    nextVisibilityMutationId = 0;
}

/**
 * @returns `true` when every key in `updates` matches the same field on `existing` (strict equality).
 */
function areSpaceUpdatesNoop (existing: Space, updates: Partial<SpacePayload>): boolean {
    return Object.entries(updates).every(([key, value]) => {
        return (existing as unknown as Record<string, unknown>)[key] === value;
    });
}

/**
 * When the API omits `isStarred` (non–user-specific payloads), keep the value already in the list
 * so PUTs like visibility do not clear Favorites UI until a response includes star metadata.
 */
function mergeIncomingSpaceWithCachedStar (incoming: Space, existing: Space | undefined): Space {
    if (Object.prototype.hasOwnProperty.call(incoming, 'isStarred')) {
        return incoming;
    }
    if (existing?.isStarred === undefined) {
        return incoming;
    }

    return { ...incoming, isStarred: existing.isStarred };
}

/**
 * When the API omits `viewerCanvasViewport` (e.g. some PUT responses), keep the cached value so
 * we do not drop saved pan/zoom until a response includes it again.
 */
function mergeIncomingSpaceWithCachedViewerViewport (
    incoming: Space,
    existing: Space | undefined,
): Space {
    if (Object.prototype.hasOwnProperty.call(incoming, 'viewerCanvasViewport')) {
        return incoming;
    }
    if (existing?.viewerCanvasViewport === undefined) {
        return incoming;
    }

    return { ...incoming, viewerCanvasViewport: existing.viewerCanvasViewport };
}

/**
 * When the API omits `can` (e.g. some PUT responses), keep the cached value so permission-gated UI
 * does not fall back to a no-`can` state until a response includes it again.
 */
function mergeIncomingSpaceWithCachedCan (
    incoming: Space,
    existing: Space | undefined,
): Space {
    if (Object.prototype.hasOwnProperty.call(incoming, 'can')) {
        return incoming;
    }
    if (existing?.can === undefined) {
        return incoming;
    }

    return { ...incoming, can: existing.can };
}

/**
 * Re-applies list-backed user fields from the cached space when the API payload omits them.
 * Runs star, viewport, then `can` in order so each step sees the cumulative merge.
 */
function mergeIncomingSpaceWithCachedUserFields (
    incoming: Space,
    getExisting: (id: string) => Space | undefined,
): Space {
    const existing = getExisting(incoming.id);
    let merged = incoming;
    merged = mergeIncomingSpaceWithCachedStar(merged, existing);
    merged = mergeIncomingSpaceWithCachedViewerViewport(merged, existing);
    merged = mergeIncomingSpaceWithCachedCan(merged, existing);

    return merged;
}

/** Sub-pixel noise from Vue Flow measure vs stored server values */
const LAYOUT_EPS = 0.75;

/**
 * Compares layout numbers allowing small drift from Vue Flow measurement vs persisted values.
 */
function approxEqualLayout (a: number, b: number): boolean {
    return Math.abs(a - b) < LAYOUT_EPS;
}

/**
 * True when every field in `updates` already matches `node` (no PATCH needed for layout).
 * If `node` is missing, treated as unchanged so callers skip the network call.
 */
function isSpaceItemLayoutUnchangedWithNode (
    node: SpaceNode | undefined,
    updates: Partial<Pick<SpaceNode, 'x' | 'y' | 'width' | 'height'>>,
): boolean {
    if (!node) {
        return true;
    }
    if (updates.x !== undefined && !approxEqualLayout(updates.x, node.x ?? 0)) {
        return false;
    }
    if (updates.y !== undefined && !approxEqualLayout(updates.y, node.y ?? 0)) {
        return false;
    }
    if (updates.width !== undefined && !approxEqualLayout(updates.width, node.width)) {
        return false;
    }
    if (updates.height !== undefined && !approxEqualLayout(updates.height, node.height)) {
        return false;
    }

    return true;
}

/** `useList` `updateItem`: merge `(id, patch)` or single field `(id, key, value)`. */
type UpdateSpaceInListFn = {
    (id: string, patch: Partial<Space>): void;
    (id: string, key: string, value: unknown): void;
};

interface UseSpacesReturn {
    fetchingSpaces: Ref<boolean>;
    spaces: ComputedRef<Space[]>;
    addNodeToBacklogOfSpace: (spaceId: string, node: SpaceNodePayload) => Promise<SpaceNode>;
    addSpace: (item: Space, key?: string) => void;
    clearAllSpaces: () => Promise<void>;
    cleanupSpaces: () => void;
    clearSpaces: () => void;
    createSpace: (space: SpacePayload) => Promise<Space>;
    deleteSpace: (id: string) => Promise<void>;
    fetchSpace: (id: string, options?: { force?: boolean; includeItems?: boolean }) => Promise<Space>;
    fetchSpaces: () => Promise<void>;
    getSpaceById: (id: string) => Space | undefined;
    keepAliveSpaces: () => void;
    deleteNodeFromSpace: (spaceId: string, nodeId: string) => Promise<void>;
    getParentFrameColor: (spaceId: string, nodeId: string) => string | undefined;
    refreshSpaceNode: (spaceId: string, nodeId: string) => Promise<SpaceNode | undefined>;
    removeNodeFromCanvas: (spaceId: string, nodeId: string) => Promise<void>;
    updateNodeData: (
        spaceId: string,
        nodeId: string,
        updates: { data?: Record<string, unknown>; name?: string },
    ) => Promise<SpaceNode | undefined>;
    removeSpace: (id: string) => void;
    summarizeSpace: (id: string) => Promise<Space>;
    assessSpace: (id: string) => Promise<Space>;
    updateSpace: (id: string, updates: Partial<SpacePayload>) => Promise<Space>;
    updateSpaceInList: UpdateSpaceInListFn;
    setSpaceStarred: (spaceId: string, starred: boolean) => Promise<void>;
    setSpaceVisibility: (spaceId: string, visibility: 'public' | 'private') => Promise<void>;
    isSpaceItemLayoutUnchanged: (
        spaceId: string,
        itemId: string,
        updates: Partial<Pick<SpaceNode, 'x' | 'y' | 'width' | 'height'>>,
    ) => boolean;
}

/**
 * Global-ish spaces list + API orchestration (fetch, CRUD, backlog/canvas helpers, pending layout ops).
 *
 * @returns Refs, computed flags, and methods backed by `spaces.api` and `useList('spaces')`
 */
export function useSpaces (): UseSpacesReturn {
    const {
        item: getSpaceById,
        items: spaces,
        addItem: addSpace,
        addItems: addSpaces,
        clearList: clearSpaces,
        updateItem: updateSpaceInList,
        removeItem: removeSpace,
        cleanup: cleanupSpaces,
        keepAlive: keepAliveSpaces,
    } = useList('spaces');
    const { addLocalLog } = useSpacesActivityLog();
    // Shared store — `findNode` returns `undefined` in non-canvas contexts (landing, modals),
    // so the vue-flow sync below becomes a safe no-op off-canvas.
    const { findNode } = useVueFlow('space-flow');

    const typedSpaces = computed(() => spaces.value as Space[]);

    async function fetchSpaces () {
        fetchingSpaces.value = true;

        try {
            const data = await fetchSpacesApi();
            // merge already cached nodes in with the list of spaces from the BE
            const cachedNodesBySpaceId = new Map<string, SpaceNode[]>();
            for (const cached of typedSpaces.value) {
                if (cached.nodes.length > 0) {
                    cachedNodesBySpaceId.set(cached.id, cached.nodes);
                }
            }

            clearSpaces();
            serverConfirmedStarredBySpace.clear();
            serverConfirmedVisibilityBySpace.clear();

            const merged = data.map((incoming): Space => {
                const cachedNodes = cachedNodesBySpaceId.get(incoming.id);
                if (cachedNodes && incoming.nodes.length === 0) {
                    return { ...incoming, nodes: cachedNodes };
                }

                return incoming;
            });
            addSpaces(merged);
            merged.forEach(recordServerStarVisibilityBaselines);
        } catch (error) {
            clearSpaces();
            serverConfirmedStarredBySpace.clear();
            serverConfirmedVisibilityBySpace.clear();
            throw error;
        } finally {
            fetchingSpaces.value = false;
        }
    }

    async function fetchSpace (
        id: string,
        { force = false, includeItems = false } = {},
    ): Promise<Space> {
        const existing = getSpaceById(id) as Space | undefined;
        if (existing && !force && !includeItems) {
            return existing;
        }

        const data = await fetchSpaceByIdApi(
            id,
            includeItems ? { includeItems: true } : undefined,
        );
        const merged = mergeIncomingSpaceWithCachedUserFields(data, (sid) => getSpaceById(sid) as Space | undefined);
        addSpace(merged);
        recordServerStarVisibilityBaselines(merged);

        return merged;
    }

    async function createSpace (space: SpacePayload) {
        const data = await createSpaceApi(space);
        const merged = mergeIncomingSpaceWithCachedUserFields(data, (sid) => getSpaceById(sid) as Space | undefined);
        addSpace(merged);
        recordServerStarVisibilityBaselines(merged);

        return merged;
    }

    async function updateSpace (id: string, updates: Partial<SpacePayload>) {
        const existing = getSpaceById(id) as Space | undefined;
        if (existing && areSpaceUpdatesNoop(existing, updates)) {
            return existing;
        }
        const data = await updateSpaceApi(id, updates);
        const merged = mergeIncomingSpaceWithCachedUserFields(data, (sid) => getSpaceById(sid) as Space | undefined);
        addSpace(merged);
        serverConfirmedVisibilityBySpace.set(id, merged.visibility ?? 'private');
        if (Object.prototype.hasOwnProperty.call(data as object, 'isStarred')) {
            serverConfirmedStarredBySpace.set(id, merged.isStarred === true);
        }

        return merged;
    }

    async function addNodeToBacklogOfSpace (spaceId: string, node: SpaceNodePayload): Promise<SpaceNode> {
        const createdNode = await addNodeToSpaceApi(spaceId, node);
        const space = getSpaceById(spaceId) as Space | undefined;
        if (space) {
            updateSpaceInList(spaceId, 'nodes', [...space.nodes, createdNode]);
        }

        return createdNode;
    }

    async function clearAllSpaces () {
        await clearAllSpacesApi();
        clearSpaces();
        serverConfirmedStarredBySpace.clear();
        serverConfirmedVisibilityBySpace.clear();
    }

    async function deleteSpace (id: string) {
        await deleteSpaceApi(id);
        removeSpace(id);
        serverConfirmedStarredBySpace.delete(id);
        serverConfirmedVisibilityBySpace.delete(id);
    }

    function getParentFrameColor (spaceId: string, nodeId: string): string | undefined {
        const space = getSpaceById(spaceId) as Space | undefined;
        const spaceNode = space?.nodes.find((node) => node.id === nodeId);
        if (!spaceNode?.parentItemId) return undefined;

        const parentNode = space?.nodes.find((node) => node.id === spaceNode.parentItemId);

        return (parentNode?.data as Record<string, unknown>)?.color as string | undefined;
    }

    async function deleteNodeFromSpace (spaceId: string, nodeId: string): Promise<void> {
        const space = getSpaceById(spaceId) as Space | undefined;
        const currentNode = space?.nodes.find((node) => node.id === nodeId);
        if (!space || !currentNode) return;

        addLocalLog({
            action: 'delete',
            itemType: currentNode.kind,
            itemId: currentNode.id,
            itemDisplayName: currentNode.name,
            userId: space.ownerUserId,
        });

        updateSpaceInList(
            spaceId,
            'nodes',
            space.nodes.filter((node) => node.id !== nodeId),
        );

        try {
            await deleteSpaceItemApi(spaceId, nodeId);
        } catch (error) {
            const latestSpace = getSpaceById(spaceId) as Space | undefined;
            if (latestSpace) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    [...latestSpace.nodes, currentNode],
                );
            }
            throw error;
        }
    }

    async function setSpaceStarred (spaceId: string, starred: boolean) {
        const previous = getSpaceById(spaceId) as Space | undefined;
        if (!serverConfirmedStarredBySpace.has(spaceId) && previous) {
            serverConfirmedStarredBySpace.set(spaceId, previous.isStarred === true);
        }
        const mutationId = ++nextStarToggleMutationId;
        pendingStarToggleMutationIdBySpace.set(spaceId, mutationId);

        if (previous) {
            updateSpaceInList(spaceId, { isStarred: starred });
        }
        try {
            await setSpaceStarredApi(spaceId, starred);
            if (pendingStarToggleMutationIdBySpace.get(spaceId) !== mutationId) {
                return;
            }
            pendingStarToggleMutationIdBySpace.delete(spaceId);
            serverConfirmedStarredBySpace.set(spaceId, starred);
            updateSpaceInList(spaceId, { isStarred: starred });
        } catch (error) {
            if (pendingStarToggleMutationIdBySpace.get(spaceId) !== mutationId) {
                return;
            }
            pendingStarToggleMutationIdBySpace.delete(spaceId);
            if (previous) {
                const rollbackStarred = serverConfirmedStarredBySpace.get(spaceId) ?? false;
                updateSpaceInList(spaceId, { isStarred: rollbackStarred });
            }
            throw error;
        }
    }

    async function setSpaceVisibility (spaceId: string, visibility: 'public' | 'private') {
        const previous = getSpaceById(spaceId) as Space | undefined;
        if (!serverConfirmedVisibilityBySpace.has(spaceId) && previous) {
            serverConfirmedVisibilityBySpace.set(spaceId, previous.visibility ?? 'private');
        }
        const mutationId = ++nextVisibilityMutationId;
        pendingVisibilityMutationIdBySpace.set(spaceId, mutationId);

        if (previous) {
            updateSpaceInList(spaceId, { visibility });
        }
        try {
            const data = await updateSpaceApi(spaceId, { visibility });
            if (pendingVisibilityMutationIdBySpace.get(spaceId) !== mutationId) {
                return;
            }
            pendingVisibilityMutationIdBySpace.delete(spaceId);
            const merged = mergeIncomingSpaceWithCachedUserFields(
                data,
                (sid) => getSpaceById(sid) as Space | undefined,
            );
            addSpace(merged);
            serverConfirmedVisibilityBySpace.set(spaceId, merged.visibility ?? 'private');
            if (Object.prototype.hasOwnProperty.call(data as object, 'isStarred')) {
                serverConfirmedStarredBySpace.set(spaceId, merged.isStarred === true);
            }
        } catch (error) {
            if (pendingVisibilityMutationIdBySpace.get(spaceId) !== mutationId) {
                return;
            }
            pendingVisibilityMutationIdBySpace.delete(spaceId);
            if (previous) {
                const rollbackVisibility = serverConfirmedVisibilityBySpace.get(spaceId) ?? 'private';
                updateSpaceInList(spaceId, { visibility: rollbackVisibility });
            }
            throw error;
        }
    }

    async function removeNodeFromCanvas (spaceId: string, nodeId: string): Promise<void> {
        const space = getSpaceById(spaceId) as Space | undefined;
        const currentNode = space?.nodes.find((node) => node.id === nodeId);
        if (!space || !currentNode) return;
        if (currentNode.isBacklog) return;

        addLocalLog({
            action: 'canvas-to-backlog',
            itemType: currentNode.kind,
            itemId: currentNode.id,
            itemDisplayName: currentNode.name,
            userId: space.ownerUserId,
        });

        const backlogNode = { ...currentNode, isBacklog: true, parentItemId: null };
        updateSpaceInList(
            spaceId,
            'nodes',
            space.nodes.map((node) => (node.id === nodeId ? backlogNode : node)),
        );

        try {
            const persistedNode = await updateSpaceItemApi(spaceId, nodeId, { isBacklog: true, parentItemId: null });
            const latestSpace = getSpaceById(spaceId) as Space | undefined;
            if (latestSpace) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    latestSpace.nodes.map((node) => (node.id === nodeId ? persistedNode : node)),
                );
            }
        } catch (error) {
            const latestSpace = getSpaceById(spaceId) as Space | undefined;
            if (latestSpace) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    latestSpace.nodes.map((node) => (node.id === nodeId ? currentNode : node)),
                );
            }
            throw error;
        }
    }

    async function updateNodeData (
        spaceId: string,
        nodeId: string,
        updates: { data?: Record<string, unknown>; name?: string },
    ): Promise<SpaceNode | undefined> {
        const space = getSpaceById(spaceId) as Space | undefined;
        const currentNode = space?.nodes.find((node) => node.id === nodeId);
        if (!space || !currentNode) return undefined;

        const optimisticNode: SpaceNode = {
            ...currentNode,
            ...(updates.name !== undefined ? { name: updates.name } : {}),
            ...(updates.data !== undefined ? { data: { ...currentNode.data, ...updates.data } } : {}),
        };
        updateSpaceInList(
            spaceId,
            'nodes',
            space.nodes.map((node) => (node.id === nodeId ? optimisticNode : node)),
        );

        // vue-flow keeps its own node copy in `activeNodes`, so the rendered component
        // only re-renders when we change vue-flow's store directly.
        const flowNode = findNode(nodeId);
        const previousFlowData = flowNode?.data;
        if (flowNode) {
            flowNode.data = {
                ...flowNode.data,
                ...(updates.name !== undefined ? { name: updates.name } : {}),
                ...(updates.data !== undefined
                    ? { data: { ...(flowNode.data?.data ?? {}), ...updates.data } }
                    : {}),
            };
        }

        try {
            const persistedNode = await updateSpaceItemApi(spaceId, nodeId, updates);
            const latestSpace = getSpaceById(spaceId) as Space | undefined;
            if (latestSpace) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    latestSpace.nodes.map((node) => (node.id === nodeId ? persistedNode : node)),
                );
            }

            return persistedNode;
        } catch (error) {
            const latestSpace = getSpaceById(spaceId) as Space | undefined;
            if (latestSpace) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    latestSpace.nodes.map((node) => (node.id === nodeId ? currentNode : node)),
                );
            }
            if (flowNode && previousFlowData) {
                flowNode.data = previousFlowData;
            }
            throw error;
        }
    }

    async function summarizeSpace (id: string): Promise<Space> {
        const data = await summarizeSpaceApi(id);
        addSpace(data);

        return data;
    }

    async function assessSpace (id: string): Promise<Space> {
        const data = await assessSpaceApi(id);
        const merged = mergeIncomingSpaceWithCachedUserFields(data, (sid) => getSpaceById(sid) as Space | undefined);
        addSpace(merged);
        recordServerStarVisibilityBaselines(merged);

        return merged;
    }

    async function refreshSpaceNode (spaceId: string, nodeId: string): Promise<SpaceNode | undefined> {
        try {
            const freshNode = await fetchSpaceItemApi(spaceId, nodeId);
            const space = getSpaceById(spaceId) as Space | undefined;
            if (space) {
                updateSpaceInList(
                    spaceId,
                    'nodes',
                    space.nodes.map((node) => (node.id === nodeId ? freshNode : node)),
                );
            }

            return freshNode;
        } catch {
            return undefined;
        }
    }

    function isSpaceItemLayoutUnchanged (
        spaceId: string,
        itemId: string,
        updates: Partial<Pick<SpaceNode, 'x' | 'y' | 'width' | 'height'>>,
    ): boolean {
        const space = getSpaceById(spaceId) as Space | undefined;
        const node = space?.nodes.find((spaceNode) => spaceNode.id === itemId);

        return isSpaceItemLayoutUnchangedWithNode(node, updates);
    }

    return {
        fetchingSpaces,
        spaces: typedSpaces,

        addNodeToBacklogOfSpace,
        addSpace,
        deleteNodeFromSpace,
        clearAllSpaces,
        cleanupSpaces,
        clearSpaces,
        createSpace,
        deleteSpace,
        fetchSpace,
        fetchSpaces,
        getParentFrameColor,
        getSpaceById,
        keepAliveSpaces,
        refreshSpaceNode,
        removeNodeFromCanvas,
        removeSpace,
        updateNodeData,
        summarizeSpace,
        assessSpace,
        updateSpace,
        updateSpaceInList,
        setSpaceStarred,
        setSpaceVisibility,
        isSpaceItemLayoutUnchanged,
    };
}
